// The menu's «Герои / Артефакты» page: a tab bar over two browse-only panels — the heroes screen
// (heroSelect.ts, unchanged, with its own plate hidden) and the artefact guide (artifactsGuide.ts). The page
// owns «Назад» and Esc for both tabs; the last tab shown is remembered in this browser.
import { renderHeroSelect } from './heroSelect.js';
import { renderArtifactsGuide } from './artifactsGuide.js';
import { t } from './i18n/index.js';

export type CodexTab = 'heroes' | 'artifacts';
const tabs = (): [CodexTab, string][] => [['heroes', t('codex.heroes')], ['artifacts', t('codex.artifacts')]];
export const CODEX_TAB_KEY = 'inkbound.codexTab';

export function readCodexTab(): CodexTab {
  try { return localStorage.getItem(CODEX_TAB_KEY) === 'artifacts' ? 'artifacts' : 'heroes'; } catch { return 'heroes'; }
}
function writeCodexTab(tab: CodexTab) {
  try { localStorage.setItem(CODEX_TAB_KEY, tab); } catch { /* storage blocked: the tab just isn't remembered */ }
}

export type CodexHandle = { destroy(): void };

export function mountCodex(root: HTMLElement, opts: { onBack(): void }): CodexHandle {
  root.innerHTML = `
    <div class="hs-plate cx-plate">
      <button type="button" class="hs-back" id="cxBack">${t('common.back')}</button>
      <div class="cx-tabs" role="tablist" aria-label="${t('menu.heroes')}">${tabs().map(([id, label]) =>
        `<button type="button" role="tab" class="cx-tab" id="cxTab-${id}" data-tab="${id}" aria-controls="cxPanel">${label}</button>`).join('')}</div>
    </div>
    <div class="cx-panel" id="cxPanel" role="tabpanel"></div>`;
  const panel = root.querySelector('#cxPanel') as HTMLElement;
  const tabEls = [...root.querySelectorAll<HTMLButtonElement>('.cx-tab')];
  let current: CodexTab | null = null, view: { destroy(): void } | null = null;

  function show(tab: CodexTab) {
    if (tab === current) return;
    current = tab;
    writeCodexTab(tab);
    for (const el of tabEls) {
      const on = el.dataset.tab === tab;
      el.setAttribute('aria-selected', String(on));
      el.setAttribute('tabindex', on ? '0' : '-1');  // roving tabindex: Tab enters the bar once, arrows move within it
      el.classList.toggle('on', on);
    }
    panel.setAttribute('aria-labelledby', `cxTab-${tab}`);
    view?.destroy();
    view = tab === 'heroes' ? renderHeroSelect(panel, { title: t('codex.heroes') }) : renderArtifactsGuide(panel);
  }

  for (const el of tabEls) el.addEventListener('click', () => show(el.dataset.tab as CodexTab));
  const tablist = root.querySelector('.cx-tabs') as HTMLElement;
  tablist.addEventListener('keydown', e => {
    const at = tabEls.indexOf(e.target as HTMLButtonElement);
    if (at < 0) return;
    const next = e.key === 'ArrowRight' ? at + 1 : e.key === 'ArrowLeft' ? at - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? tabEls.length - 1 : null;
    if (next === null) return;
    e.preventDefault();
    const el = tabEls[(next + tabEls.length) % tabEls.length]!;
    show(el.dataset.tab as CodexTab);
    el.focus();
  });
  root.querySelector('#cxBack')!.addEventListener('click', opts.onBack);
  // On window: Esc works wherever focus is. defaultPrevented: someone nearer the target already handled it.
  function onEscape(e: KeyboardEvent) {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    e.preventDefault();
    opts.onBack();
  }
  window.addEventListener('keydown', onEscape);

  const first = readCodexTab();
  show(first);
  // the heroes panel focuses its selected card itself; the guide leaves focus on the tab bar
  if (first === 'artifacts') tabEls.find(el => el.dataset.tab === first)!.focus({ preventScroll: true });
  return {
    destroy() {
      window.removeEventListener('keydown', onEscape);
      view?.destroy(); view = null;
      root.innerHTML = '';
    },
  };
}
